import {Component, OnInit} from '@angular/core';
import {IFeaturedProductsView, IProduct} from "../../../product/product-models";
import {ISponsorDetails} from "../../../social/social-models";
import {LandingService} from "../../../landing/landing.service";
import {ITranslationRequest, TranslateService} from "../../../translation/translate.service";
import {SponsorService} from "../../../common/sponsor.service";
import {UserService} from "../../../common/user.service";
import {catchError, find, map} from "rxjs/operators";
import {ShareableUtil} from "../../../social/shareable-util";
import {Observable, throwError} from "rxjs";
import {CartService} from "../../../cart/cart.service";
import {AlertUtilService} from "../../../alert/alert-util.service";
import {CartUtil} from "../../../cart/cart-util";
import {CustomerUtil} from "../../../util/customer-util";
import {Constants} from "../../../constants";

@Component({
    selector: 'featured-products-row-guided',
    templateUrl: './featured-products-row-guided.component.html',
    styleUrls: ['./featured-products-row-guided.component.scss'],
    standalone: false
})
export class FeaturedProductsRowGuidedComponent implements OnInit {

    products: IProduct[];
    translatedTitle: string;
    showAddToCart: boolean = false;
    addingAllToCart: boolean = false;
    useLocaleOverride: boolean;

    constructor(
        private userService: UserService,
        private landingService: LandingService,
        private translateService: TranslateService,
        private sponsorService: SponsorService,
        private cartService: CartService,
        private alertUtilService: AlertUtilService
    ) {
    }

    ngOnInit() {
        this.getProducts();
    }

    getProducts(): void {
        this.landingService.getLandingFeaturedProducts().subscribe((featuredProductsView: IFeaturedProductsView) => {
            this.products = featuredProductsView.featuredProducts;
            this.translateTitle(featuredProductsView);
        });
    }

    getRecommendedBy(): Observable<string> {
        return this.sponsorService.fetchSponsorDetails(this.userService.getShopUserSnapshot().customer.sponsorId, false).pipe(
            find((sponsorDetails: ISponsorDetails) => ShareableUtil.isSponsorDetailsPresent(sponsorDetails)),
            map((sponsorDetails: ISponsorDetails) => CustomerUtil.getSponsorDisplayName(sponsorDetails)));
    }

    translateTitle(featuredProductsView: IFeaturedProductsView): void {
        let translationTag: string = "cart.landing.featured.products";

        this.showAddToCart = this.userService.getShopUserSnapshot().config.showGuidedShopping && !featuredProductsView.usanaDefault;

        if (this.showAddToCart && !featuredProductsView.shareListType?.includes(Constants.LIST_TYPE_SKIN_ANALYZER_LIST)) {
            this.getRecommendedBy().subscribe(recommendedBy => {
                if (recommendedBy) {
                    const translationRequest: ITranslationRequest = {tag: "cart.landing.featured.products.recommended.by", replacements: new Map([['name', recommendedBy]])};
                    this.translateService.translateRequest(translationRequest, this.useLocaleOverride).then((translation: string) => {
                        this.translatedTitle = translation;
                    });
                }
            });
        } else {
            if (featuredProductsView.shareListType?.includes(Constants.LIST_TYPE_SKIN_ANALYZER_LIST)) {
                translationTag = "cart.landing.featured.products.skin.analyzer";
            }
            this.translateService.translate(translationTag, this.useLocaleOverride).then((translation: string) => {
                this.translatedTitle = translation;
            });
        }
    }

    addAllToCart() {
        if (!this.addingAllToCart) {
            this.addingAllToCart = true;

            this.userService.getShopUser().pipe(
                map(user => {
                    const productsToAdd = this.products.filter(product =>
                        CartUtil.showAddToCartButtonProduct(product, user)
                    );
                    const hasUnavailableProducts = productsToAdd.length !== this.products.length;
                    this.adjustQuantityForProducts(productsToAdd, [], hasUnavailableProducts);
                })
            ).subscribe();
        }
    }

    adjustQuantityForProducts(productsToAdd: IProduct[], addedProducts: IProduct[], hasUnavailableProducts: boolean, index?: number) {
        index = index || 0;

        if (index <= productsToAdd.length - 1) {
            this.adjustQuantity(productsToAdd[index], addedProducts).subscribe((success: boolean) => {
                if (!success) {
                    hasUnavailableProducts = true;
                }
                this.adjustQuantityForProducts(productsToAdd, addedProducts, hasUnavailableProducts, index + 1);
            }, () => { // if any product fails to add to cart
                hasUnavailableProducts = true;
            });
        } else {
            this.handleProductsAddedAlert(hasUnavailableProducts, addedProducts);
        }
    }

    adjustQuantity(product: IProduct, addedProducts: IProduct[]): Observable<boolean> {
        return this.cartService.adjustCartQuantity(product, 1, undefined).pipe(
            map((success: boolean) => {
                if (success) {
                    addedProducts.push(product);
                }
                return success;
            }),
            catchError((error: any) => throwError(error))
        );
    }

    handleProductsAddedAlert(hasUnavailableProducts: boolean, addedProducts: IProduct[]) {
        this.addingAllToCart = false;
        if (addedProducts.length == 1) {
            this.alertUtilService.alertAddedToCartWithQuantity(addedProducts[0], 1);
        } else if (addedProducts.length > 1) {
            this.alertUtilService.alertAddedAllToCartWithQuantity(addedProducts.length);
        } else {
            // no success alert. no products added.
        }

        if (hasUnavailableProducts) {
            this.alertUtilService.addWarning({body: "cart.some.products.not.added", translate: true});
        }
    }

}
